import type { ActivityContext, ActivityHandle } from '../../core/activity';
import { isEmbed } from '../../core/embed';
import levelsData from './levels.json';

type ChartType = 'pie' | 'rectangle';

interface SubLevel {
  chartType: ChartType;
  fixedNumerator: boolean;
  fixedDenominator: boolean;
  maxFractions?: number;
  random?: boolean;
}

interface LevelData {
  shuffle: boolean;
  subLevels: SubLevel[];
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: LevelData[];
}

interface LevelsFile {
  levels: Level[];
}

const LEVELS = (levelsData as LevelsFile).levels;

export function mountFractionsFind(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const subLevels = buildSubLevels(level);
  let subIndex = 0;
  let cfg = subLevels[0]!;
  let numerator = 0;
  let denominator = 0;
  let numInput = '';
  let denInput = '';
  let correctCount = 0;
  let errosRodada = 0;
  let errosTotal = 0;
  let tentativas = 0;
  let aguardando = false; // trava o Confirmar durante a transição de acerto

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Ler frações</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage fractions">
      <div class="feedback" aria-live="polite"></div>
      <p class="fractions__progress" aria-live="polite"></p>
      <h2 class="fractions__question"></h2>
      <div class="fractions__lay">
        <div class="fractions__chart-wrap"></div>
        <div class="fractions__fraction">
          <div class="fractions__row">
            <input class="fractions__numerator" type="text" inputmode="numeric" maxlength="2" aria-label="Partes pintadas (numerador)">
            <span class="fractions__cap">pintadas</span>
          </div>
          <div class="fractions__bar" aria-hidden="true"></div>
          <div class="fractions__row">
            <input class="fractions__denominator" type="text" inputmode="numeric" maxlength="2" aria-label="Total de partes (denominador)">
            <span class="fractions__cap">total de partes</span>
          </div>
        </div>
      </div>
      <p class="fractions__dica" aria-live="polite" hidden></p>
      <button class="btn btn--primary fractions__ok" type="button">Confirmar</button>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.fractions__progress')!;
  const questionEl = page.querySelector<HTMLElement>('.fractions__question')!;
  const chartWrap = page.querySelector<HTMLElement>('.fractions__chart-wrap')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const dicaEl = page.querySelector<HTMLElement>('.fractions__dica')!;
  const numInputEl = page.querySelector<HTMLInputElement>('.fractions__numerator')!;
  const denInputEl = page.querySelector<HTMLInputElement>('.fractions__denominator')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.fractions__ok')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  numInputEl.addEventListener('input', () => {
    numInputEl.value = numInputEl.value.replace(/\D/g, '').slice(0, 2);
    numInput = numInputEl.value;
  });
  denInputEl.addEventListener('input', () => {
    denInputEl.value = denInputEl.value.replace(/\D/g, '').slice(0, 2);
    denInput = denInputEl.value;
  });

  function nomeParte(): string {
    return cfg.chartType === 'pie' ? 'fatias' : 'partes';
  }

  function startRound(): void {
    cfg = subLevels[subIndex]!;
    denominator = randInt(2, 12);
    numerator = randInt(1, denominator - 1);
    errosRodada = 0;
    aguardando = false;
    if (cfg.fixedNumerator) numInput = String(numerator);
    else numInput = '';
    if (cfg.fixedDenominator) denInput = String(denominator);
    else denInput = '';
    dicaEl.hidden = true;
    dicaEl.textContent = '';
    renderProgress();
    renderQuestion();
    renderChart(false);
    renderInputs();
  }

  function renderProgress(): void {
    progressEl.textContent = `Questão ${subIndex + 1} de ${subLevels.length}`;
  }

  // A pergunta EXPLÍCITA, sempre visível — "Encontre o numerador" pressupõe
  // exatamente o que a atividade quer ensinar (numerador = partes pintadas)
  // e derrubou usuário real que contou as partes brancas.
  function renderQuestion(): void {
    const p = nomeParte();
    if (cfg.fixedDenominator && !cfg.fixedNumerator) {
      questionEl.textContent = `O desenho tem ${denominator} ${p}. Quantas estão pintadas?`;
    } else if (cfg.fixedNumerator && !cfg.fixedDenominator) {
      questionEl.textContent = `Em quantas ${p} o desenho foi dividido? Conte todas, pintadas e brancas.`;
    } else {
      questionEl.textContent = 'Que fração do desenho está pintada?';
    }
  }

  function renderChart(comNumeros: boolean): void {
    chartWrap.innerHTML = '';
    // Numera só o que a pergunta manda contar: numerar a fatia branca quando
    // se perguntam as pintadas convidaria a responder o número errado.
    const numeros: ModoNumeros = !comNumeros
      ? 'nenhum'
      : (cfg.fixedDenominator && !cfg.fixedNumerator ? 'pintadas' : 'todas');
    if (cfg.chartType === 'pie') chartWrap.appendChild(renderPie(numerator, denominator, numeros));
    else chartWrap.appendChild(renderRectangle(numerator, denominator, numeros));
  }

  function renderInputs(): void {
    numInputEl.value = numInput;
    denInputEl.value = denInput;
    numInputEl.disabled = cfg.fixedNumerator;
    denInputEl.disabled = cfg.fixedDenominator;
    numInputEl.classList.toggle('fractions__input--locked', cfg.fixedNumerator);
    denInputEl.classList.toggle('fractions__input--locked', cfg.fixedDenominator);
    focoNoLivre();
  }

  function focoNoLivre(): void {
    if (!cfg.fixedNumerator) numInputEl.focus();
    else if (!cfg.fixedDenominator) denInputEl.focus();
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  // Ajuda progressiva: 1º erro = dica em texto; 2º em diante = os números
  // aparecem no próprio desenho pra pessoa contar em cima deles.
  function mostraDica(n: number, d: number): void {
    const p = nomeParte();
    let texto: string;
    if (!cfg.fixedNumerator && !isNaN(n) && n === denominator - numerator) {
      texto = `Você contou as ${p} brancas. Conte as pintadas de laranja!`;
    } else if (!cfg.fixedDenominator && !isNaN(d) && d === numerator) {
      texto = `Embaixo vai o TOTAL de ${p} do desenho, pintadas e brancas.`;
    } else if (cfg.fixedDenominator) {
      texto = `Conte só as ${p} pintadas de laranja e escreva o número em cima.`;
    } else if (cfg.fixedNumerator) {
      texto = `Conte todas as ${p} do desenho, pintadas e brancas, e escreva embaixo.`;
    } else {
      texto = `Em cima vão as ${p} pintadas; embaixo, o total de ${p}.`;
    }
    if (errosRodada >= 2) {
      renderChart(true);
      texto += ' Os números estão no desenho pra ajudar.';
    }
    dicaEl.textContent = texto;
    dicaEl.hidden = false;
  }

  function validate(): void {
    if (aguardando) return;
    const n = parseInt(numInput, 10);
    const d = parseInt(denInput, 10);
    tentativas++;
    if (!isNaN(n) && !isNaN(d) && n === numerator && d === denominator) {
      aguardando = true;
      ctx.audio.play('correct');
      showFeedback('ok');
      dicaEl.hidden = true;
      correctCount++;
      subIndex++;
      window.setTimeout(() => {
        if (subIndex < subLevels.length) startRound();
        else finishLevel();
      }, 700);
      return;
    }
    errosRodada++;
    errosTotal++;
    ctx.audio.play('wrong');
    showFeedback('err');
    mostraDica(n, d);
    // Limpa o campo errado e devolve o foco — digitar de novo sem apagar
    if (!cfg.fixedNumerator && n !== numerator) { numInput = ''; numInputEl.value = ''; }
    if (!cfg.fixedDenominator && d !== denominator) { denInput = ''; denInputEl.value = ''; }
    focoNoLivre();
  }

  okBtn.addEventListener('click', validate);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      validate();
      e.preventDefault();
    }
  };
  window.addEventListener('keydown', onKey);

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount, {
      total: subLevels.length,
      tentativas,
      erros: errosTotal,
    });
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    const temProximo = level.level < LEVELS.length;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${subLevels.length} frações.</p>
      <div class="activity-finish__actions">
        ${isEmbed
          ? `<button class="btn btn--secondary" type="button" data-act="denovo">Treinar de novo</button>`
          : `<button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>`}
        ${temProximo
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    const hub = stage.querySelector<HTMLElement>('[data-act="hub"]');
    if (hub) hub.addEventListener('click', () => ctx.onExit());
    const denovo = stage.querySelector<HTMLElement>('[data-act="denovo"]');
    if (denovo) {
      denovo.addEventListener('click', () => {
        irPara(`/fractions_find/${level.level}`);
      });
    }
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        irPara(`/fractions_find/${level.level + 1}`);
      });
    }
  }

  // Navegação interna sem prefixo (#/adulto persiste como MODO no router).
  // Se o hash já for o destino, força o hashchange — senão o botão morre.
  function irPara(path: string): void {
    if (window.location.hash === `#${path}`) {
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } else {
      window.location.hash = path;
    }
  }

  startRound();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}

type ModoNumeros = 'nenhum' | 'pintadas' | 'todas';

function renderPie(numerator: number, denominator: number, numeros: ModoNumeros): SVGElement {
  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 6;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
  svg.setAttribute('class', 'fractions__chart fractions__chart--pie');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', `Pizza dividida em ${denominator} fatias, ${numerator} pintadas`);

  for (let i = 0; i < denominator; i++) {
    const startAngle = (i * 2 * Math.PI) / denominator - Math.PI / 2;
    const endAngle = ((i + 1) * 2 * Math.PI) / denominator - Math.PI / 2;
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    path.setAttribute('d', d);
    path.setAttribute('class', i < numerator ? 'fractions__slice fractions__slice--filled' : 'fractions__slice');
    svg.appendChild(path);

    if (numeros === 'todas' || (numeros === 'pintadas' && i < numerator)) {
      const meio = (startAngle + endAngle) / 2;
      const tx = cx + r * 0.62 * Math.cos(meio);
      const ty = cy + r * 0.62 * Math.sin(meio);
      svg.appendChild(numeroDaParte(tx, ty, i + 1, i < numerator));
    }
  }
  return svg;
}

function renderRectangle(numerator: number, denominator: number, numeros: ModoNumeros): SVGElement {
  const W = 320;
  const H = 120;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.setAttribute('class', 'fractions__chart fractions__chart--rect');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', `Retângulo dividido em ${denominator} partes, ${numerator} pintadas`);

  const partW = W / denominator;
  for (let i = 0; i < denominator; i++) {
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('x', String(i * partW));
    rect.setAttribute('y', '0');
    rect.setAttribute('width', String(partW));
    rect.setAttribute('height', String(H));
    rect.setAttribute('class', i < numerator ? 'fractions__cell fractions__cell--filled' : 'fractions__cell');
    svg.appendChild(rect);

    if (numeros === 'todas' || (numeros === 'pintadas' && i < numerator)) {
      svg.appendChild(numeroDaParte(i * partW + partW / 2, H / 2, i + 1, i < numerator));
    }
  }
  return svg;
}

// O número de contagem dentro da parte (ajuda do 2º erro em diante)
function numeroDaParte(x: number, y: number, n: number, pintada: boolean): SVGElement {
  const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  text.setAttribute('x', String(x));
  text.setAttribute('y', String(y));
  text.setAttribute('class', pintada ? 'fractions__num fractions__num--pintada' : 'fractions__num');
  text.setAttribute('text-anchor', 'middle');
  text.setAttribute('dominant-baseline', 'central');
  text.textContent = String(n);
  return text;
}

function buildSubLevels(level: Level): SubLevel[] {
  const result: SubLevel[] = [];
  level.data.forEach((d) => {
    const list = d.shuffle ? shuffle([...d.subLevels]) : d.subLevels;
    result.push(...list);
  });
  return result;
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

function randInt(min: number, max: number): number {
  if (max < min) return min;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
